import type { Meta, Video } from './index.ts';
import { isLiveChannel } from './live.ts';

export type NextEpisode = { status: 'ready'; video: Video } | { status: 'unreleased'; video: Video } |
  { status: 'not-series' | 'missing-metadata' | 'ambiguous' | 'last' };

/** Catalog policy belongs to Streamit. Never synthesize video IDs or jump across
 * contradictory metadata; a user can still choose an episode explicitly. */
export function nextEpisode(meta: Meta, currentId: string, now = Date.now()): NextEpisode {
  if (meta.type !== 'series' || isLiveChannel(meta)) return { status: 'not-series' };
  if (!Number.isFinite(now) || !meta.videos?.length || meta.videos.length > 2000) return { status: 'missing-metadata' };
  const videos = new Map<string, Video>(), coordinates = new Map<string, string>();
  for (const value of meta.videos) {
    if (!value || typeof value.id !== 'string' || !value.id || value.id.length > 180 || /[\r\n\0]/.test(value.id) ||
        !Number.isSafeInteger(value.season) || value.season! < 0 || !Number.isSafeInteger(value.episode) || value.episode! < 0)
      return { status: 'missing-metadata' };
    const coordinate = `${value.season}:${value.episode}`, prior = videos.get(value.id), at = coordinates.get(coordinate);
    if ((prior && (prior.season !== value.season || prior.episode !== value.episode || prior.released !== value.released)) || (at && at !== value.id))
      return { status: 'ambiguous' };
    videos.set(value.id, value); coordinates.set(coordinate, value.id);
  }
  if (!videos.has(currentId)) return { status: 'missing-metadata' };
  const ordered = [...videos.values()].sort((a, b) => a.season! - b.season! || a.episode! - b.episode!);
  const video = ordered[ordered.findIndex(v => v.id === currentId) + 1];
  if (!video) return { status: 'last' };
  if (video.released) {
    const release = Date.parse(video.released);
    if (!Number.isFinite(release)) return { status: 'missing-metadata' };
    if (release > now) return { status: 'unreleased', video };
  }
  return { status: 'ready', video };
}
